import styled from "@emotion/styled"
import * as uniform from "@uniformdev/context-react"
import ImageCLD from "components/media/ImageCLD"
import { useController } from "components/nav-v2/bento/Controller"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { contrastMode } from "css/a11y"
import getMediaQuery, { breakpoint } from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { bold, legend90 } from "css/text"
import { useMotionValueEvent } from "framer-motion"
import useSSR from "hooks/useSSR"
import { useLayoutEffect, useRef } from "react"
import { useNav } from "../Wrapper"

const Container = styled.div`
  position: relative;
  width: calc(100% + var(--inset-inline, 0rem) * 2);
  inset-inline-start: calc(var(--inset-inline, 0rem) * -1);
  overflow: hidden;

  @media (min-width: ${breakpoint.m}) {
    &[data-may-be-vertical="true"] {
      --inset-inline: 0rem;

      & .bento-deck {
        flex-direction: column;
        gap: 0.5rem;
      }
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  /* padding-block: 5px; */
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &.focus-visible {
    outline: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &.bento-deck {
    padding-block: 5px;
  }
`

const Bento = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.38rem;
  flex: 0 0 calc(100% - var(--inset-inline, 0rem) * 2);

  @media (min-width: ${breakpoint.xxll}) {
    .sub-pane[data-expandable="true"] & {
      flex-basis: calc(41.5325rem - var(--inset-inline, 0rem) * 1.5);
    }
  }

  scroll-snap-align: start;
  scroll-snap-stop: always;
  padding-inline-start: var(--inset-inline, 0rem);

  align-content: stretch;

  height: auto;
  align-content: stretch;

  &:last-of-type {
    margin-inline-end: var(--inset-inline, 0rem);
  }

  aspect-ratio: 21.9 / 17.4;

  @media (min-width: ${breakpoint.m}) {
    .bento-container[data-may-be-vertical="true"] & {
      margin-inline-end: var(--inset-inline, 0rem);
    }
  }

  @media (min-width: ${breakpoint.m}) {
    aspect-ratio: 37.8 / 30.1;
    gap: 0.5rem;
  }

  > li {
    list-style: none;
    margin: 0;
    padding: 0;
    min-height: 0;
    display: grid;
  }

  > li > a {
    & * {
      pointer-events: none;
    }

    container-name: bento-cell;
    container-type: size;
    display: block;
    position: relative;
    min-height: 0;
    overflow: hidden;
    border-radius: 0.38rem;

    @media (any-hover: hover) and (any-pointer: fine) {
      &:hover {
        > figure > figcaption {
          background-color: rgb(var(--pure-white));
          color: rgb(var(--green));
        }
      }
    }

    &.active {
      cursor: default;
      > figure > figcaption {
        background-color: rgb(var(--pure-white));
        color: rgb(var(--green));
      }
    }
    > figure {
      display: contents;

      > picture,
      > img {
        width: 100%;
        height: 100%;
        background: lightgrey;

        object-fit: cover;
        width: 100%;
        height: 100%;

        &.bento-cell-asset-portrait,
        &.bento-cell-asset-landscape {
          display: none;
        }
        &.bento-cell-asset-squarish {
          display: block;
        }

        @container bento-cell (orientation: landscape) and (min-aspect-ratio: 3 / 2) {
          &.bento-cell-asset-portrait,
          &.bento-cell-asset-squarish {
            display: none;
          }

          [data-variant="retailer"] & {
            display: block !important;
          }

          &.bento-cell-asset-landscape {
            display: block;
          }
        }

        @container bento-cell (orientation: portrait) {
          &.bento-cell-asset-landscape,
          &.bento-cell-asset-squarish {
            display: none;
          }
          &.bento-cell-asset-portrait {
            display: block;
          }
        }
      }
      > figcaption,
      > img {
        grid-area: 1 / 1;
      }
    }
    > figcaption,
    > img {
      grid-area: 1 / 1;
    }
  }

  /* 1) Pattern xxxx: 1 square */
  &[data-pattern="xxxx"] {
    grid-template-columns: 1fr;
  }
  &[data-pattern="xxxx"] > :nth-child(1) {
    grid-column: 1;
  }

  /* 2) Pattern xyxy: 2 portraits */
  &[data-pattern="xyxy"] {
    grid-template-columns: repeat(2, 1fr);
  }
  &[data-pattern="xyxy"] > :nth-child(1) {
    grid-column: 1;
  }
  &[data-pattern="xyxy"] > :nth-child(2) {
    grid-column: 2;
  }

  /* Helper: 4 columns, 2 rows grid base */
  &[data-pattern="xyxz"],
  &[data-pattern="xxzz"],
  &[data-pattern="xyzz"],
  &[data-pattern="xyza"],
  &[data-pattern="xyzy"],
  &[data-pattern="xxyy"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }

  /* 3) Pattern xxyy: 2 landscapes */
  &[data-pattern="xxyy"] > :nth-child(1) {
    grid-column: 1 / span 2;
    grid-row: 1;
  }
  &[data-pattern="xxyy"] > :nth-child(2) {
    grid-column: 1 / span 2;
    grid-row: 2;
  }

  /* 4) Pattern xyxz: Left portrait + 2 */
  &[data-pattern="xyxz"] > :nth-child(1) {
    grid-column: 1;
    grid-row: 1 / span 2;
  }
  &[data-pattern="xyxz"] > :nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }
  &[data-pattern="xyxz"] > :nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }

  /* 5) Pattern xyzy: Right portrait + 2 */
  &[data-pattern="xyzy"] > :nth-child(1) {
    grid-column: 1;
    grid-row: 1;
  }
  &[data-pattern="xyzy"] > :nth-child(2) {
    grid-column: 1;
    grid-row: 2;
  }
  &[data-pattern="xyzy"] > :nth-child(3) {
    grid-column: 2;
    grid-row: 1 / span 2;
  }

  /* 6) Pattern xxzz: Top landscape + 2 */
  &[data-pattern="xxyz"] > :nth-child(1) {
    grid-column: 1 / span 2;
    grid-row: 1;
  }
  &[data-pattern="xxyz"] > :nth-child(2) {
    grid-column: 1;
    grid-row: 2;
  }
  &[data-pattern="xxyz"] > :nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }

  /* 7) Pattern xyzz: Bottom landscape + 2 */
  &[data-pattern="xyzz"] > :nth-child(1) {
    grid-column: 1;
    grid-row: 1;
  }
  &[data-pattern="xyzz"] > :nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }
  &[data-pattern="xyzz"] > :nth-child(3) {
    grid-column: 1 / span 2;
    grid-row: 2;
  }

  /* 8) Pattern xyza: 4 squares */
  &[data-pattern="xyza"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto auto;
  }
  &[data-pattern="xyza"] > :nth-child(1) {
    grid-column: 1;
    grid-row: 1;
  }
  &[data-pattern="xyza"] > :nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }
  &[data-pattern="xyza"] > :nth-child(3) {
    grid-column: 1;
    grid-row: 2;
  }
  &[data-pattern="xyza"] > :nth-child(4) {
    grid-column: 2;
    grid-row: 2;
  }
`

const Figcaption = styled.figcaption`
  /* ${legend90} */
  font-size: 12px;
  ${bold}
  line-height: 0.875rem;
  position: absolute;
  inset-block-end: 0;
  inset-inline: 0;
  width: calc(100% - calc(1.2rem + calc(0.38rem * 2)));
  color: rgb(var(--pure-white));
  background: linear-gradient(231deg, rgba(255, 255, 255, 0) -3.16%, rgba(255, 255, 255, 0.2) 67.73%);
  border: 0.75px solid rgba(var(--pure-white) / 0.1);
  padding: 0.5rem 0.6rem;
  height: fit-content;
  margin: 0 auto;
  align-self: end;
  z-index: 2;
  margin-block-end: 0.38rem;
  border-radius: 0.38rem;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-wrap: pretty balance;
  transition: color 0.3s ease, background-color 0.3s ease;

  &.dark-theme {
    color: rgb(var(--pure-white));
  }

  &.light-theme {
    color: rgb(var(--light-black));
  }

  ${getMediaQuery("m")} {
    font-size: 14px;
  }

  ${contrastMode}
`

const CellWrapper = styled.div`
  container-name: bento-cell;
  container-type: size;
  position: relative;
  min-height: 0;
  overflow: hidden;
  border-radius: 0.38rem;

  > a {
    & * {
      pointer-events: none;
    }

    display: block;
    width: 100%;
    height: 100%;

    > picture,
    > img {
      width: 100%;
      height: 100%;
      background: lightgrey;

      object-fit: cover;
      width: 100%;
      height: 100%;

      &.bento-cell-asset-portrait,
      &.bento-cell-asset-landscape {
        display: none;
      }
      &.bento-cell-asset-squarish {
        display: block;
      }

      @container bento-cell (orientation: landscape) and (min-aspect-ratio: 3 / 2) {
        &.bento-cell-asset-portrait,
        &.bento-cell-asset-squarish {
          display: none;
        }
        &.bento-cell-asset-landscape {
          display: block;
        }
      }

      @container bento-cell (orientation: portrait) {
        &.bento-cell-asset-landscape,
        &.bento-cell-asset-squarish {
          display: none;
        }
        &.bento-cell-asset-portrait {
          display: block;
        }
      }
    }

    // [data-may-be-vertical="false"] & {
    /*&.active {
      cursor: default;
    }*/
    // }
  }

  > figcaption {
    pointer-events: none;
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:has(> a:hover) > figure > figcaption {
      background-color: rgb(var(--pure-white));
      color: rgb(var(--green));
    }
  }

  &:has(> a.active) > figure > figcaption {
    background-color: rgb(var(--pure-white));
    color: rgb(var(--green));
  }
  // }
`

const Nav = styled.nav`
  position: relative;
  margin: 0 auto;
  width: calc(100% - (var(--inset-inline, 0rem) * 2));
  display: flex;
  padding-block-start: 1.5rem;
  padding-block-end: 0.5rem;

  @media (hover: hover) and (pointer: fine) {
    padding-block-end: 0.75rem;
    padding-block-start: 0.95rem;
  }

  align-items: center;
  justify-content: center;

  // gap: 0.25rem;

  [data-slides="1"] &,
  &[inert] {
    display: none !important;
  }

  & ul {
    all: unset;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  & li {
    all: unset;
    display: contents;
  }

  & button {
    ${buttonReset}
    display: flex;
    height: 1.5rem;
    align-items: center;
    .bento-carousel-dot {
      all: unset;
      display: inline-block;
      width: 0.875rem;
      background: #d4d4d4;
      height: 0.25rem;
      transition: width 0.3s ease-in-out, background 0.4s ease-in-out;
      border-radius: 9999px;
      cursor: pointer;
      margin-inline: 5px;
    }
  }

  & button[aria-current="true"] .bento-carousel-dot {
    width: 1.5rem;
    margin-inline: 0;
    background: rgb(var(--green));
  }

  & button[data-index]:disabled {
    margin-inline: 5px;
  }

  // & button[data-index]:disabled:first-of-type {
  //   margin-inline-start: 0;
  // }

  // & button[data-index]:disabled:not(:has(~ button[data-index])) {
  //   margin-inline-end: 0;
  // }

  & button.bento-carousel-arrow {
    all: unset;
    position: absolute;
    width: 2.25rem;
    aspect-ratio: 1;
    display: grid;
    place-items: center;
    transition: opacity 0.3s ease-in-out;
    opacity: 1;
    background-color: #ebebeb;
    border-radius: 9999px;
    background-position: center center;
    background-size: 1.5rem;
    background-repeat: no-repeat;

    &[data-dir="-1"] {
      inset-inline-end: 3.25rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M11.4,1.9L5.6,7.5l5.8,5.6L9.5,15L1.6,7.5L9.5,0L11.4,1.9z'/%3E%3C/svg%3E");
    }
    &[data-dir="1"] {
      inset-inline-end: 0;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M3.5,13.1l5.8-5.6L3.5,1.9l2-1.9l7.9,7.5L5.5,15L3.5,13.1z'/%3E%3C/svg%3E");
    }

    display: none;

    [dir="rtl"] & {
      &[data-dir="-1"] {
        // inset-inline-end: 0;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M3.5,13.1l5.8-5.6L3.5,1.9l2-1.9l7.9,7.5L5.5,15L3.5,13.1z'/%3E%3C/svg%3E");
      }
      &[data-dir="1"] {
        // inset-inline-end: 3.25rem;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M11.4,1.9L5.6,7.5l5.8,5.6L9.5,15L1.6,7.5L9.5,0L11.4,1.9z'/%3E%3C/svg%3E");
      }
    }

    @media (hover: hover) and (pointer: fine) {
      display: grid;
      &:hover {
        cursor: pointer;

        &[data-dir="-1"] {
          background-image: url("data:image/svg+xml,%3Csvg style='color:rgb(18, 119, 73); transition: color 300ms ease;' xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M11.4,1.9L5.6,7.5l5.8,5.6L9.5,15L1.6,7.5L9.5,0L11.4,1.9z'/%3E%3C/svg%3E");
        }

        &[data-dir="1"] {
          background-image: url("data:image/svg+xml,%3Csvg style='color:rgb(18, 119, 73); transition: color 300ms ease;' xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M3.5,13.1l5.8-5.6L3.5,1.9l2-1.9l7.9,7.5L5.5,15L3.5,13.1z'/%3E%3C/svg%3E");
        }

        [dir="rtl"] & {
          &[data-dir="-1"] {
            // inset-inline-end: 0;
            background-image: url("data:image/svg+xml,%3Csvg style='color:rgb(18, 119, 73); transition: color 300ms ease;' xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M3.5,13.1l5.8-5.6L3.5,1.9l2-1.9l7.9,7.5L5.5,15L3.5,13.1z'/%3E%3C/svg%3E");
          }

          &[data-dir="1"] {
            // inset-inline-end: 3.25rem;
            background-image: url("data:image/svg+xml,%3Csvg style='color:rgb(18, 119, 73); transition: color 300ms ease;' xmlns='http://www.w3.org/2000/svg' width='10px' height='5px' viewBox='0 0 15 15'%3E%3Cpath fill='currentColor' d='M11.4,1.9L5.6,7.5l5.8,5.6L9.5,15L1.6,7.5L9.5,0L11.4,1.9z'/%3E%3C/svg%3E");
          }
        }
      }
    }

    .bento-container[data-index="0"] &[data-dir="-1"] {
      opacity: 0.5;
      pointer-events: none;
    }
    .bento-container[data-index="0"][data-slides="1"] &[data-dir="1"],
    .bento-container[data-index="1"][data-slides="2"] &[data-dir="1"],
    .bento-container[data-index="2"][data-slides="3"] &[data-dir="1"],
    .bento-container[data-index="3"][data-slides="4"] &[data-dir="1"],
    .bento-container[data-index="4"][data-slides="5"] &[data-dir="1"],
    .bento-container[data-index="5"][data-slides="6"] &[data-dir="1"],
    .bento-container[data-index="6"][data-slides="7"] &[data-dir="1"],
    .bento-container[data-index="7"][data-slides="8"] &[data-dir="1"],
    .bento-container[data-index="8"][data-slides="9"] &[data-dir="1"],
    .bento-container[data-index="9"][data-slides="10"] &[data-dir="1"] {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`

const patterns = [
  { pattern: "xxxx", cells: 1 },
  { pattern: "xyxy", cells: 2 },
  { pattern: "xxyy", cells: 2 },
  { pattern: "xyxz", cells: 3 },
  { pattern: "xyzy", cells: 3 },
  { pattern: "xxyz", cells: 3 },
  { pattern: "xyzz", cells: 3 },
  { pattern: "xyza", cells: 4 },
]

const fallbackByCells = {
  1: ["xxxx"],
  2: ["xyxy", "xxyy"],
  3: ["xyxz", "xyzy", "xxyz", "xyzz"],
  4: ["xyza"],
}

const pickPattern = (requestedPattern, itemCount) => {
  const requested = patterns.find(p => p.pattern === requestedPattern)
  if (requested?.cells === itemCount) return requestedPattern

  const list = fallbackByCells[itemCount] ?? []
  return list[0] ?? "xxxx"
}

const CustomImage = ({ children, ...props }) => {
  return <img srcSet={props.srcSet} sizes={props.sizes} alt='' />
}

const ImageSet = ({ children, pattern, ...props }) => {
  const console = useConsole()
  const ssr = useSSR()
  const { landscape, portrait, squarish } = (props.sources ?? []).reduce((set, source) => {
    const { width, height } = source
    const clone = structuredClone(props)

    if (width / height > 1.5) {
      set.landscape = Object.assign(clone, { sources: [source] })
    } else if (width / height < 2 / 3) {
      set.portrait = Object.assign(clone, { sources: [source] })
    } else {
      set.squarish = Object.assign(clone, { sources: [source] })
    }

    return set
  }, {})

  console.silly("Nav:Bento:ImageSet(%o)", { landscape, portrait, squarish })
  if (ssr) return null
  return (
    <>
      {portrait && <ImageCLD {...portrait} className='bento-cell-asset-portrait' sizes={`(min-width: ${breakpoint.s}) 21rem, 50vw`} alt='' />}
      {landscape && <ImageCLD {...landscape} className='bento-cell-asset-landscape' sizes={`(min-width: ${breakpoint.s}) 41.5325rem, 90vw`} alt='' />}
      {squarish && (
        <ImageCLD
          {...squarish}
          className='bento-cell-asset-squarish'
          sizes={pattern === "xxxx" ? `(min-width: ${breakpoint.s}) 41.5325rem, 90vw` : `(min-width: ${breakpoint.s}) 21rem, 50vw`}
          loading='lazy'
          alt=''
        />
      )}
    </>
  )
}

const Compartment = props => {
  const console = useConsole()
  const navigation = useNavigation()
  const itemCount = props.items?.length ?? 0
  const nav = useNav()
  const appliedPattern = pickPattern(props.type, itemCount)
  const isLinkActive = item => item.href === navigation.slug
  const navigate = e => {
    e.preventDefault()

    if (e.target.href === global.location.href) {
      nav.collapseAll()
      return
    }

    navigation.navigate(e.currentTarget.href)
  }
  // const close = e => {
  //   e.preventDefault()
  //   nav.collapseAll()
  // }

  console.verbose("Nav:Bento:Compartment(%o)", { isLinkActive, props })
  return (
    <Bento className='bento' data-pattern={appliedPattern} data-type={props.type}>
      {props.items.map((item, i) => {
        return (
          <li key={i}>
            <a
              className={isLinkActive(item) ? "active" : ""}
              aria-current={isLinkActive(item) ? "page" : undefined}
              href={navigation.localize(item.href)}
              onClick={e => {
                /*isLinkActive(item) ? close(e) : */ navigate(e)
              }}
            >
              <figure>
                {item.assets?.sources?.length ? (
                  <ImageSet pattern={appliedPattern} {...item.assets} />
                ) : item.assets?.srcSet ? (
                  <CustomImage {...item.assets} />
                ) : (
                  <picture />
                )}
                <Figcaption className={item?.theme?.theme ? item.theme.theme : `dark-theme`} dangerouslySetInnerHTML={{ __html: item.label }} />
              </figure>
            </a>
          </li>
        )
      })}
    </Bento>
  )
}

const Deck = props => {
  const console = useConsole()
  const bentoController = useController()
  const scores = uniform.useScores()
  const rwrapper = useRef(null)
  const rcontainer = useRef(null)
  const rnav = useRef(null)
  const dictionary = useDictionary()
  const cells = props.items
    .map(({ item, item_p13n }) => {
      if (item) return item

      const variants = item_p13n.variants
        .map(({ variant }) => variant)
        .sort((a, b) => {
          if (!Array.isArray(a.criteria?.name?.crit) && Array.isArray(b.criteria?.name?.crit)) return 1
          if (Array.isArray(a.criteria?.name?.crit) && !Array.isArray(b.criteria?.name?.crit)) return -1
          return 0
        })

      for (const variant of variants) {
        if (!variant.criteria?.name?.crit) {
          return variant
        }

        const hit = variant.criteria.name.crit.every(crit => {
          const { l: signal, op: operator, r: value } = crit

          switch (operator) {
            case "=":
              return +scores[signal] === +value
            default:
              return false
          }
        })

        if (hit) {
          return variant
        }
      }
      return null
    })
    .filter(Boolean)
  const compartments = props.grids.map(({ grid }) => {
    const pattern = grid.pattern
    const cells = pattern.includes("a") ? 4 : pattern.includes("z") ? 3 : pattern.includes("y") ? 2 : 1

    return {
      cells,
      pattern,
    }
  })

  if (!compartments.length) {
    compartments.push({ cells: 4, pattern: "xyza" })
  }

  const bentos = []
  let i = 0
  let j = 0
  while (true) {
    i = i >= compartments.length ? 0 : i
    const compartment = compartments[i]

    const bento = { type: compartment.pattern, cells: compartment.cells, items: [] }
    for (let k = 0; k < compartment.cells; k += 1) {
      if (cells[j]) {
        bento.items.push(cells[j])
        j += 1
      }
    }
    bentos.push(bento)

    if (j >= cells.length) {
      break
    }

    i += 1
  }

  useMotionValueEvent(bentoController.motion, "change", latest => {
    /* make sure the scrolling position stay in place during open animations */
    const previous = bentoController.motion.getPrevious()

    if (latest > previous || !latest) {
      /** verticalIfDesktop indicates that we are on the main nav. */
      const activeSlide = !bentoController.verticalIfDesktop && rwrapper.current.querySelector(".bento:has(.active)")
      const index = activeSlide?.dataset?.index ?? 0
      const left = activeSlide ? activeSlide.offsetLeft : 0
      rwrapper.current.scrollTo({ left, behavior: "instant" })
      rcontainer.current.dataset.index = index
    }
  })

  useLayoutEffect(function buildCarousel() {
    const controller = new AbortController()
    const carousel = rcontainer.current
    const deck = rwrapper.current
    const nav = rnav.current

    const mql = global.matchMedia(`(min-width: ${breakpoint.m})`)

    const ul = document.createElement("ul")
    for (const [i, slide] of deck.querySelectorAll(".bento").entries()) {
      const li = document.createElement("li")
      const button = document.createElement("button")
      li.appendChild(button)
      const span = button.appendChild(document.createElement("span"))
      button.type = "button"
      button.dataset.index = i
      span.dataset.index = i
      slide.dataset.index = i
      span.classList.add("bento-carousel-dot")
      button.ariaLabel = dictionary.ariaLabelSlideNumOutOf(i + 1, deck.querySelectorAll(".bento").length)
      button.setAttribute("aria-current", i === 0 ? "true" : "false")
      ul.setAttribute("aria-label", dictionary.pagination())
      ul.appendChild(li)
      button.style.setProperty("cursor", i === 0 ? "default" : "pointer")
      if (i === 0) button.setAttribute("disabled", "")
      span.style.setProperty("pointer-events", "none")
      nav.appendChild(button)
      button.addEventListener(
        "click",
        () => {
          slide.scrollIntoView({ behavior: "smooth" })
        },
        { signal: controller.signal }
      )
    }
    nav.appendChild(ul)

    carousel.dataset.slides = ul.children.length
    if (+carousel.dataset.slides > 1) {
      for (const dir of [-1, 1]) {
        const button = document.createElement("button")
        button.type = "button"
        button.classList.add("bento-carousel-arrow")
        button.ariaLabel = dir === -1 ? dictionary.ariaLabelPreviousSlide() : dictionary.ariaLabelNextSlide()
        if (dir === -1) button.setAttribute("disabled", "")
        button.dataset.dir = dir
        nav.appendChild(button)

        button.addEventListener(
          "click",
          e => {
            const current = +carousel.dataset.index
            const dir = +e.target.dataset.dir
            const next = Math.max(0, Math.min(current + dir, carousel.dataset.slides - 1))
            const target = nav.querySelector(`[data-index="${next}"]`)
            target.click()
          },
          { signal: controller.signal }
        )
      }
    }

    deck.addEventListener(
      "scroll",
      () => {
        const dir = document.documentElement.getAttribute("dir") === "rtl" ? -1 : 1
        const index = Math.round(deck.scrollLeft / deck.clientWidth) * dir
        carousel.dataset.index = index
        const dots = nav.querySelectorAll("button:not(.bento-carousel-arrow)")
        const arrows = nav.querySelectorAll("button.bento-carousel-arrow")
        arrows[0].toggleAttribute("disabled", index <= 0)
        arrows[1].toggleAttribute("disabled", index >= carousel.dataset.slides - 1)
        dots.forEach(dot => {
          dot.toggleAttribute("disabled", index === +dot.dataset.index)
        })
        for (const dot of dots) {
          dot.setAttribute("aria-current", "false")
          dot.style.setProperty("cursor", "pointer")
        }
        dots[index]?.setAttribute("aria-current", "true")
        dots[index]?.style.setProperty("cursor", "default")
      },
      { signal: controller.signal }
    )

    /* Chrome/Safari do not scroll an overflow:auto wrapper when focus moves to an
       element that is clipped by an overflow:hidden ancestor */
    deck.addEventListener(
      "focusin",
      e => {
        const slide = e.target.closest(".bento")
        if (!slide) return
        requestAnimationFrame(() => {
          deck.scrollTo({ left: slide.offsetLeft, behavior: "instant" })
        })
      },
      { signal: controller.signal }
    )

    if (bentoController.verticalIfDesktop) {
      const onmqlchange = () => {
        nav.inert = mql.matches
      }
      mql.addEventListener("change", onmqlchange, { signal: controller.signal })
      onmqlchange()
    }

    return () => {
      controller.abort()
      nav.innerHTML = ""
    }
  })

  console.verbose("Nav:Bento:Deck(%o)", { props, bentos, compartments, cells, controller: bentoController })
  return (
    <Container className='bento-container' ref={rcontainer} data-may-be-vertical={bentoController.verticalIfDesktop} data-index='0'>
      <Wrapper className='bento-deck' tabIndex='-1' ref={rwrapper}>
        {bentos.map((bento, i) => (
          <Compartment key={i} {...bento} />
        ))}
      </Wrapper>
      <Nav className='bento-controls' ref={rnav} />
    </Container>
  )
}

export default Deck
